import { mountShell } from "./shared/shell";
import { isLoggedIn, apiFetch } from "./shared/api";
import { qs, escapeHtml } from "./shared/dom";
import { formatDate } from "./shared/format";
import { MARKETING, GROUNDWORK_METHOD } from "../../src/content/marketing";
import { FUNCTIONAL_TESTS } from "../../src/content/tests";
import { img } from "./shared/images";
import type { AssessmentSummary } from "../../src/lib/scoring";
import type { WorkoutCompletion } from "../../src/lib/records";
import { buildWeeklyProgram, nextRetestDate } from "../../src/lib/program";
import {
  renderHero,
  renderStatGrid,
  renderMethod,
  renderCompareTable,
  renderFaqs,
  renderStories,
  renderCta,
} from "./shared/marketingView";

function renderLandingPage(): string {
  const page = MARKETING.index!;
  const heroImage = img("hero-home.png", "Athlete building foundational strength", "16/9");
  return `
    ${renderHero(page, heroImage, "/login.html")}
    ${renderStatGrid(page)}
    ${renderMethod(GROUNDWORK_METHOD)}
    ${renderCompareTable(page)}
    ${renderFaqs(page)}
    ${renderStories(page.memberStories)}
    ${renderCta(page, "/login.html")}
  `;
}

function testName(testId: string): string {
  return FUNCTIONAL_TESTS.find((t) => t.id === testId)?.name ?? testId;
}

function testSkillTag(testId: string): string | null {
  return FUNCTIONAL_TESTS.find((t) => t.id === testId)?.targetSkill ?? null;
}

function renderProgramCard(summary: AssessmentSummary): string {
  const schedule = buildWeeklyProgram(summary);
  const retestOn = nextRetestDate(summary.lastSessionOn);
  const rows = schedule
    .map((day) => {
      const cell = day.workoutSlug
        ? `<a href="/workout.html?slug=${encodeURIComponent(day.workoutSlug)}">${escapeHtml(day.label)}</a>`
        : `<span style="color:var(--muted);">${escapeHtml(day.label)}</span>`;
      return `<li><strong>${day.day}</strong> &mdash; ${cell}</li>`;
    })
    .join("");

  return `
    <div class="card">
      <h2 class="section-title" style="font-size:1.05rem;">Your Weekly Program</h2>
      <p class="section-lead" style="margin-bottom:0.75rem;">Built from your lowest test scores. Repeat this week${
        retestOn ? ` until your next retest, around ${formatDate(retestOn)}.` : "."
      }</p>
      <ul style="list-style:none;padding:0;margin:0;">${rows}</ul>
    </div>
  `;
}

async function renderMemberHome(): Promise<string> {
  const [summary, completionsRes] = await Promise.all([
    apiFetch<AssessmentSummary>("/api/assessments/summary"),
    apiFetch<{ completions: WorkoutCompletion[] }>("/api/completions"),
  ]);
  const completions = completionsRes.completions.slice(0, 5);
  const pct = Math.round((summary.total / summary.maxTotal) * 100);

  const priorities = summary.priorities.length
    ? summary.priorities
        .map((id) => {
          const tag = testSkillTag(id);
          const label = escapeHtml(testName(id));
          return tag
            ? `<li><a href="/workouts.html?tag=${encodeURIComponent(tag)}">${label}</a></li>`
            : `<li>${label}</li>`;
        })
        .join("")
    : `<li>Complete your first assessment to find your priorities.</li>`;

  const recent = completions.length
    ? completions.map((c) => `<li>${escapeHtml(c.workoutSlug)} &mdash; ${formatDate(c.completedAt)}</li>`).join("")
    : `<li class="empty-state" style="padding:0.5rem 0;">No workouts logged yet.</li>`;

  const nextStep = summary.lastSessionOn
    ? `<a class="btn" href="/workouts.html">Go to today's workout</a>`
    : `<a class="btn" href="/tests.html">Take the Functional Tests</a>`;

  return `
    <h1 class="display" style="font-size:1.9rem;">Welcome back</h1>
    <div class="card">
      <h2 class="section-title" style="font-size:1.1rem;">Your Score</h2>
      <div class="progress-bar"><span style="width:${pct}%"></span></div>
      <p class="section-lead" style="margin-bottom:0;">${summary.total} / ${summary.maxTotal}</p>
    </div>
    <div class="card-grid">
      <div class="card">
        <h2 class="section-title" style="font-size:1.05rem;">Training Priorities</h2>
        <ul>${priorities}</ul>
        <a href="/tests.html">View all tests &rarr;</a>
      </div>
      <div class="card">
        <h2 class="section-title" style="font-size:1.05rem;">Recent Workouts</h2>
        <ul>${recent}</ul>
        <a href="/workouts.html">View all workouts &rarr;</a>
      </div>
    </div>
    ${renderProgramCard(summary)}
    <div style="margin-top:1.5rem;">${nextStep}</div>
  `;
}

async function init(): Promise<void> {
  await mountShell();
  const main = qs<HTMLElement>("main");
  if (isLoggedIn()) {
    main.innerHTML = await renderMemberHome();
  } else {
    main.innerHTML = renderLandingPage();
  }
}

init();
